import React, { type MutableRefObject, useRef, useState } from "react";
import type { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
  type GridFilterModel,
  type GridPaginationModel,
  type GridRowId,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import { useQueryClient } from "@tanstack/react-query";
import { useResourceQueries } from "src/framework/useResourceQueries.ts";
import { useDataProvider } from "src/system/useDataProvider.ts";
import { type VariantResource } from "@exonn/js-sdk";
import { ResourceEnum } from "src/constants/constants.ts";
import { logger } from "src/logger";
import { ProductFakeServerTreeDataGroupingColumn } from "src/components/DataGrid/product/ProductFakeServerTreeDataGroupingColumn.tsx";
import type { GridEventListener } from "@mui/x-data-grid";
import { useNonConflictingTheme } from "src/lib/emotion/use-non-conflicting-theme.ts";
import { getColumns } from "src/admin/lists/product/getColumns.tsx";
import {
  type ProductRowResource,
  ProductRowType,
} from "src/framework/listV2/types.ts";
import { mapProductResourceToProductRow } from "src/admin/lists/product/mapProductResourceToProductRow.tsx";
import { prepareFilterModel } from "src/system/prepareFilterModel.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

const getRowParams = ({ row }: { row: ProductRowResource }) => {
  if ([ProductRowType.Complex, ProductRowType.Multiple].includes(row.type)) {
    return {
      resource: ResourceEnum.Variant,
      include: ["components", "product", "images"],
      filter: {
        items: [
          {
            field: "product_id",
            value: row.id,
            operator: "exact",
          },
        ],
      },
    };
  }
  return {
    resource: ResourceEnum.VariantComponent,
    include: [
      "componentVariant",
      "componentVariant.product",
      "variant.product",
      "variant.images",
    ],
    filter: {
      items: [
        {
          field: row.type === ProductRowType.Set ? "product_id" : "variant_id",
          value: row.id,
          operator: "exact",
        },
      ],
    },
  };
};

export const useProductGridProps = ({
  resource,
  locale,
  apiRef,
  onRowClick: onRowClickProp,
  expandRowOnRowClick,
  withoutDescendants,
  appliedFilterModel,
  includes,
}: {
  resource: string;
  locale: string;
  expandRowOnRowClick: boolean;
  withoutDescendants?: boolean;
  apiRef: MutableRefObject<GridApiPremium>;
  onRowClick?: GridEventListener<"rowClick">;
  appliedFilterModel: GridFilterModel;
  includes?: string[];
}) => {
  const { t } = useErpTranslation({
    resource,
    type: "list",
  });

  const [loadingMap, setLoadingMap] = useState<Record<GridRowId, boolean>>({});
  const [errorMap, setErrorMap] = useState<Record<GridRowId, Error>>({});

  const isFetching = useRef(false);
  const queryClient = useQueryClient();
  const queries = useResourceQueries();
  const dataProvider = useDataProvider();
  const theme = useNonConflictingTheme();

  const fetchProductSubRows = async ({
    groupKeys,
    row,
    paginationModel,
    sortModel,
    includes,
  }: {
    groupKeys: string[];
    filterModel: GridFilterModel;
    paginationModel: GridPaginationModel;
    sortModel: GridSortModel;
    row: ProductRowResource;
    includes?: string[];
  }) => {
    const params = {
      groupKeys,
      sort: sortModel,
      locale,
      page: {
        size: 99999,
        number: 1,
      },
      ...getRowParams({
        row,
      }),
    };
    const response = await queryClient.fetchQuery({
      queryKey: queries.list(params),
      queryFn: async () => {
        return await dataProvider.getList(params);
      },
    });

    const nextRows = response.data;
    queryClient.setQueryData(
      queries.list({
        filter: prepareFilterModel(appliedFilterModel, getColumns({ t })),
        sort: sortModel,
        locale,
        include: includes,
        page: {
          size: paginationModel.pageSize,
          number: paginationModel.page + 1,
        },
        resource: ResourceEnum.Product,
      }),
      (prevResponse: any) => {
        if (!prevResponse) {
          return prevResponse;
        }
        return {
          ...prevResponse,
          data: [...prevResponse.data, ...nextRows],
        };
      },
    );
    return {
      rows: (nextRows as VariantResource[]).map(mapProductResourceToProductRow),
    };
  };

  if (resource === ResourceEnum.Product) {
    const expandRow = async (row: ProductRowResource) => {
      const { id } = row;
      const rowNode = apiRef.current.getRowNode(row.id);
      if (rowNode == null) {
        return;
      }
      // if a row has descendants, and it is not a group yet, it means we didn't load sub rows yet
      if (row.descendants_count > 0 && rowNode.type !== "group") {
        try {
          // if there is ongoing fetch then do nothing
          // helps to avoid double fetch on accidental double click
          if (isFetching.current) {
            return;
          }
          setLoadingMap((prev) => ({
            ...prev,
            [row.id]: true,
          }));

          setErrorMap(({ [row.id]: _, ...rest }) => ({
            ...rest,
          }));
          isFetching.current = true;
          const response = await fetchProductSubRows({
            groupKeys: [row.id],
            filterModel: apiRef.current.state.filter.filterModel,
            paginationModel: apiRef.current.state.pagination.paginationModel,
            sortModel: apiRef.current.state.sorting.sortModel,
            includes,
            row,
          });
          isFetching.current = false;
          setLoadingMap((prev) => ({
            ...prev,
            [row.id]: false,
          }));

          // put sub-rows in the datagrid rows
          apiRef.current.setRows([
            ...apiRef.current.getSortedRows(),
            ...response.rows,
          ]);

          apiRef.current.setRowChildrenExpansion(row.id, true);
        } catch (error: any) {
          setErrorMap((prev) => ({
            ...prev,
            [row.id]: error,
          }));
          setLoadingMap((prev) => ({
            ...prev,
            [row.id]: false,
          }));
          logger.error(error);
        }
      }
      if (rowNode.type === "group") {
        apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
      }
    };
    const handleRowClick: GridEventListener<"rowClick"> = (
      params,
      event,
      details,
    ) => {
      if (
        params.row.descendants_count > 0 &&
        expandRowOnRowClick &&
        !withoutDescendants
      ) {
        expandRow(params.row);
      } else {
        onRowClickProp?.(params, event, details);
      }
    };
    return {
      groupingColDef: {
        width: 60,
        headerName: t("table.columns.type"),
        hideable: false,
        disableReorder: true,
        resizable: false,
        // @ts-ignore
        renderCell: (params) => {
          return (
            <ProductFakeServerTreeDataGroupingColumn
              onFetchRows={fetchProductSubRows}
              loadingMap={loadingMap}
              withoutDescendants={withoutDescendants}
              includes={includes}
              errorMap={errorMap}
              {...params}
            />
          );
        },
      },
      rowHeight: theme.productDataGrid.rowHeight,
      onRowClick: handleRowClick,
      treeData: true,
      getTreeDataPath: (row: any) => row.path,
    };
  }

  return {};
};
